import type {
  CompositionV3BreakdownOptions,
  CompositionV3Filters,
} from '@revelio/data-access';
import { EntKind } from '@revelio/data-access';

import type { GeographyMode } from '../../../../../shared/user-preferences/types';
import {
  getSkillTierForLevelId,
  skillEntKindFromTier,
} from '../role-skill-ent-kind';

const ROLE_FILTER_LEVELS_DESC = [7, 6, 5, 4, 3, 2, 1] as const;
const SKILL_FILTER_LEVELS_DESC = [8, 7, 6, 5, 4, 3, 2, 1] as const;

export type CompositionV3BreakdownDimension =
  | 'education'
  | 'ethnicity'
  | 'gender'
  | 'geography'
  | 'rics'
  | 'role'
  | 'seniority'
  | 'skill';

function getDeepestIndexedFilterLevel(
  compositionFilters: CompositionV3Filters,
  baseKey: 'role_level' | 'skills_level'
): number | null {
  const levels =
    baseKey === 'role_level'
      ? ROLE_FILTER_LEVELS_DESC
      : SKILL_FILTER_LEVELS_DESC;
  for (const n of levels) {
    const key = `${baseKey}_${n}` as keyof CompositionV3Filters;
    const arr = compositionFilters[key];
    if (Array.isArray(arr) && arr.length > 0) {
      return n;
    }
  }
  return null;
}

// Tiers follow the `ROLE_TAXONOMY_TREE_LEVELS` steps (1 → 3 → 5 → 7), not
// consecutive `role_level_*` indices.
function roleBreakdownTierFromDeepestFilter(deepest: number | null): EntKind {
  if (deepest === null) {
    return EntKind.RoleLvl_1;
  }
  if (deepest <= 2) {
    return EntKind.RoleLvl_3;
  }
  if (deepest <= 4) {
    return EntKind.RoleLvl_5;
  }
  return EntKind.RoleLvl_7;
}

export function isLeafRoleFilter(
  compositionFilters: CompositionV3Filters
): boolean {
  return getDeepestIndexedFilterLevel(compositionFilters, 'role_level') === 7;
}

/** Role counts breakdown level for the deepest role filter (postings_v3 `roles_counts_level`). */
export function roleCountsLevelFromCompositionFilters(
  compositionFilters: CompositionV3Filters
): EntKind {
  return roleBreakdownTierFromDeepestFilter(
    getDeepestIndexedFilterLevel(compositionFilters, 'role_level')
  );
}

function effectiveCustomSkillTierFromDeepestLevel(
  customSkillDeepestLevel:
    | 'skill_0'
    | 'skill_1'
    | 'skill_2'
    | 'skill_3'
    | 'skill_4'
    | null
    | undefined
): number {
  if (
    customSkillDeepestLevel === null ||
    customSkillDeepestLevel === undefined
  ) {
    return 0;
  }
  return getSkillTierForLevelId(customSkillDeepestLevel) ?? 0;
}

function nextSkillBreakdownTier(deepestTier: number): number {
  const visibleSkillTiers = (
    ['skill_0', 'skill_1', 'skill_2', 'skill_3', 'skill_4'] as const
  )
    .map((levelId) => getSkillTierForLevelId(levelId))
    .filter((tier): tier is number => tier !== null);

  const nextVisibleTier = visibleSkillTiers.find((tier) => tier > deepestTier);
  if (nextVisibleTier !== undefined) {
    return nextVisibleTier;
  }
  return Math.min(deepestTier + 1, 8);
}

function deriveSkillBreakdownLevel(
  compositionFilters: CompositionV3Filters,
  customSkillDeepestLevel:
    | 'skill_0'
    | 'skill_1'
    | 'skill_2'
    | 'skill_3'
    | 'skill_4'
    | null
    | undefined,
  hasSkillTree: boolean
): EntKind {
  const deepestSkillsLevel = getDeepestIndexedFilterLevel(
    compositionFilters,
    'skills_level'
  );
  if (deepestSkillsLevel !== null) {
    return skillEntKindFromTier(nextSkillBreakdownTier(deepestSkillsLevel));
  }
  if (!hasSkillTree) {
    return EntKind.SkillLvl_1;
  }
  const effectiveTier = effectiveCustomSkillTierFromDeepestLevel(
    customSkillDeepestLevel
  );
  return skillEntKindFromTier(nextSkillBreakdownTier(effectiveTier));
}

function geoBreakdownLevelFromFilters(
  compositionFilters: CompositionV3Filters,
  geographyMode: GeographyMode
): EntKind {
  if (compositionFilters.city?.length) return EntKind.City;
  if (compositionFilters.msa?.length) return EntKind.City;
  if (compositionFilters.state?.length) return EntKind.City;
  if (compositionFilters.country?.length) {
    return geographyMode === 'administrative' ? EntKind.State : EntKind.Msa;
  }
  if (compositionFilters.region?.length) return EntKind.Country;
  return EntKind.Region;
}

function ricsBreakdownLevelFromFilters(
  compositionFilters: CompositionV3Filters
): EntKind {
  if (compositionFilters.rics_k400?.length) return EntKind.RicsK400;
  if (compositionFilters.rics_k50?.length) return EntKind.RicsK400;
  if (compositionFilters.rics_k10?.length) return EntKind.RicsK50;
  return EntKind.RicsK10;
}

/** Top-of-hierarchy levels for callers without filter context (e.g. tooltips). */
export function getBaseCompositionV3BreakdownLevels(
  _geographyMode?: GeographyMode
): Pick<
  CompositionV3BreakdownOptions,
  | 'role_breakdown_level'
  | 'skill_breakdown_level'
  | 'geo_breakdown_level'
  | 'rics_breakdown_level'
> {
  return {
    geo_breakdown_level: EntKind.Region,
    rics_breakdown_level: EntKind.RicsK10,
    role_breakdown_level: EntKind.RoleLvl_1,
    skill_breakdown_level: EntKind.SkillLvl_1,
  };
}

function dedupeBreakdownIntArray(
  values: ReadonlyArray<number> | null | undefined
): number[] | undefined {
  if (values === null || values === undefined) return undefined;
  if (values.length === 0) return undefined;
  return Array.from(new Set(values));
}

function shouldIncludeBreakdown(
  includedBreakdownDimensions: ReadonlySet<CompositionV3BreakdownDimension> | null,
  dimension: CompositionV3BreakdownDimension
): boolean {
  return (
    includedBreakdownDimensions === null ||
    includedBreakdownDimensions.has(dimension)
  );
}

export type CompositionV3BreakdownLevelOverrides = Partial<
  Pick<
    CompositionV3BreakdownOptions,
    | 'geo_breakdown_level'
    | 'rics_breakdown_level'
    | 'role_breakdown_level'
    | 'skill_breakdown_level'
  >
>;

// The geography/rics/role/skill id arrays are intentionally left unset so the
// backend returns the top N segments at the derived level.
export const getCompositionV3BreakdownOptions = ({
  compositionFilters,
  customSkillTree,
  customSkillDeepestLevel,
  includedBreakdownDimensions,
  geographyMode = 'msa',
  breakdownLevelOverrides,
}: {
  compositionFilters: CompositionV3Filters;
  customSkillTree?:
    | { id: string; name: string; depth: number; children?: unknown[] }[]
    | null;
  customSkillDeepestLevel?:
    | 'skill_0'
    | 'skill_1'
    | 'skill_2'
    | 'skill_3'
    | 'skill_4'
    | null;
  includedBreakdownDimensions?: readonly CompositionV3BreakdownDimension[];
  geographyMode?: GeographyMode;
  breakdownLevelOverrides?: CompositionV3BreakdownLevelOverrides;
}): CompositionV3BreakdownOptions => {
  const includedDimensions =
    includedBreakdownDimensions === undefined
      ? null
      : new Set(includedBreakdownDimensions);

  const geo_breakdown_level =
    breakdownLevelOverrides?.geo_breakdown_level ??
    geoBreakdownLevelFromFilters(compositionFilters, geographyMode);
  const rics_breakdown_level =
    breakdownLevelOverrides?.rics_breakdown_level ??
    ricsBreakdownLevelFromFilters(compositionFilters);
  const role_breakdown_level =
    breakdownLevelOverrides?.role_breakdown_level ??
    roleBreakdownTierFromDeepestFilter(
      getDeepestIndexedFilterLevel(compositionFilters, 'role_level')
    );

  const hasSkillTree =
    Array.isArray(customSkillTree) && customSkillTree.length > 0;
  const skill_breakdown_level =
    breakdownLevelOverrides?.skill_breakdown_level ??
    deriveSkillBreakdownLevel(
      compositionFilters,
      customSkillDeepestLevel,
      hasSkillTree
    );

  const education = dedupeBreakdownIntArray(compositionFilters.highest_degree);
  const ethnicity = dedupeBreakdownIntArray(compositionFilters.ethnicity);
  const gender = dedupeBreakdownIntArray(compositionFilters.gender);
  const seniority = dedupeBreakdownIntArray(compositionFilters.seniority);

  return {
    geo_breakdown_level,
    rics_breakdown_level,
    role_breakdown_level,
    skill_breakdown_level,
    ...(shouldIncludeBreakdown(includedDimensions, 'education') &&
    education?.length
      ? { education }
      : {}),
    ...(shouldIncludeBreakdown(includedDimensions, 'ethnicity') &&
    ethnicity?.length
      ? { ethnicity }
      : {}),
    ...(shouldIncludeBreakdown(includedDimensions, 'gender') && gender?.length
      ? { gender }
      : {}),
    ...(shouldIncludeBreakdown(includedDimensions, 'seniority') &&
    seniority?.length
      ? { seniority }
      : {}),
  };
};
